const MIC_CONSTRAINTS = { channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true };
export const MICROPHONE_PREFERENCE_KEY = 'instanttranscriber_recording_microphone';

export const recordingSource = input => input === 'tab-mic' ? 'recording_tab_mic' : input === 'tab' ? 'recording_tab' : 'recording_mic';

export async function listMicrophones() {
  if (!navigator.mediaDevices?.enumerateDevices) return [];
  const devices = await navigator.mediaDevices.enumerateDevices();
  return devices.filter(device => device.kind === 'audioinput' && device.deviceId && !['default', 'communications'].includes(device.deviceId));
}

// Own both capture streams and the graph, including late permission responses.
// The destination stream stays unchanged when a microphone is replaced.
export class RecordingAudioSession {
  constructor({ input, microphoneId = '', onLevels = () => {} }) {
    this.input = input;
    this.microphoneId = microphoneId;
    this.onLevels = onLevels;
    this.sources = {};
    this.closed = false;
    this.switchVersion = 0;
    this.preparing = null;
    this.displaySurface = 'unknown';
  }

  accept(stream) {
    if (!this.closed) return stream;
    stream.getTracks().forEach(track => track.stop());
    throw Object.assign(new Error('Recording cancelled'), { code: 'request_cancelled' });
  }

  async prepare() {
    if (!this.preparing) this.preparing = this.prepareStreams().catch(error => { this.close(); throw error; });
    return this.preparing;
  }

  async prepareStreams() {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      throw Object.assign(new Error('Recording unavailable'), { code: 'recording_browser_unsupported' });
    }
    // Ask for display sharing synchronously within the user's click.
    let displayRequest;
    if (this.input !== 'mic') {
      if (!navigator.mediaDevices.getDisplayMedia) throw Object.assign(new Error('Recording unavailable'), { code: 'recording_browser_unsupported' });
      displayRequest = navigator.mediaDevices.getDisplayMedia({ video: true, audio: true, systemAudio: 'include', surfaceSwitching: 'include', selfBrowserSurface: 'exclude' });
    }
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (AudioContext) {
      try {
        this.context = new AudioContext();
        this.resumePromise = Promise.resolve(this.context.resume()).catch(() => {});
      } catch { /* Direct microphone capture can work without a level meter. */ }
    }
    if (displayRequest) {
      try { this.displayStream = this.accept(await displayRequest); }
      catch (error) { error.captureStage = 'tab'; throw error; }
      const tracks = this.displayStream.getAudioTracks();
      if (!tracks.length) throw Object.assign(new Error('No shared audio'), { code: 'missing_tab_audio', captureStage: 'tab' });
      this.tabStream = new MediaStream([tracks[0]]);
      const surface = this.displayStream.getVideoTracks?.()[0]?.getSettings?.().displaySurface;
      if (['browser', 'window', 'monitor'].includes(surface)) this.displaySurface = surface;
    }
    if (this.input !== 'tab') {
      try { this.microphoneStream = this.accept(await this.requestMicrophone(this.microphoneId)); }
      catch (error) { error.captureStage = 'microphone'; throw error; }
    }
    if (this.closed) throw Object.assign(new Error('Recording cancelled'), { code: 'request_cancelled' });
    if (this.input === 'tab-mic' && this.context?.state !== 'running') {
      let timeout;
      try {
        await Promise.race([this.resumePromise, new Promise((_, reject) => { timeout = setTimeout(() => reject(Object.assign(new Error('Audio context suspended'), { code: 'recording_audio_context_unavailable' })), 4000); })]);
      } finally { clearTimeout(timeout); }
      if (this.closed) throw Object.assign(new Error('Recording cancelled'), { code: 'request_cancelled' });
      if (this.context?.state !== 'running') throw Object.assign(new Error('Audio mixing unavailable'), { code: 'recording_audio_context_unavailable' });
    }
    // Keep direct capture as a fallback for browsers that suspend Web Audio.
    // A suspended meter must never turn a valid microphone recording silent.
    if (this.context?.state === 'running' && this.context.createMediaStreamDestination) {
      this.destination = this.context.createMediaStreamDestination();
      if (this.input === 'mic') this.destination.channelCount = 1;
      this.outputStream = this.destination.stream;
    } else {
      if (this.input === 'tab-mic') throw Object.assign(new Error('Audio mixing unavailable'), { code: 'recording_audio_context_unavailable' });
      this.outputStream = this.microphoneStream || this.tabStream;
    }
    if (this.microphoneStream) this.attachSource('mic', this.microphoneStream);
    if (this.tabStream) this.attachSource('tab', this.tabStream);
    this.emitLevels();
    this.meterTimer = setInterval(() => this.emitLevels(), 150);
    return this;
  }

  requestMicrophone(deviceId) {
    return navigator.mediaDevices.getUserMedia({ audio: { ...MIC_CONSTRAINTS, ...(deviceId ? { deviceId: { exact: deviceId } } : {}) } });
  }

  attachSource(kind, stream) {
    const track = stream.getAudioTracks?.()[0] || stream.getTracks()[0];
    const source = { stream, track, seen: false, measured: false, samples: [], lowSignal: false, listeners: [] };
    if (this.context) {
      source.node = this.context.createMediaStreamSource(stream);
      source.analyser = this.context.createAnalyser();
      source.analyser.fftSize = 2048;
      source.analyser.smoothingTimeConstant = 0;
      source.data = new Float32Array(source.analyser.fftSize);
      source.node.connect(source.analyser);
      if (this.destination) source.node.connect(this.destination);
    }
    for (const event of ['mute', 'unmute', 'ended']) {
      const listener = () => this.emitLevels();
      track?.addEventListener?.(event, listener);
      source.listeners.push([event, listener]);
    }
    this.sources[kind] = source;
  }

  detachSource(source, stopTracks = true) {
    if (!source) return;
    source.listeners.forEach(([event, listener]) => source.track?.removeEventListener?.(event, listener));
    try { source.node?.disconnect(); source.analyser?.disconnect(); } catch {}
    if (stopTracks) source.stream.getTracks().forEach(track => track.stop());
  }

  get canSwitchMicrophone() { return Boolean(this.destination && this.sources.mic && !this.closed); }

  async replaceMicrophone(deviceId) {
    if (!this.canSwitchMicrophone) throw Object.assign(new Error('Input fixed for this recording'), { code: 'microphone_switch_unavailable' });
    const version = ++this.switchVersion;
    const stream = this.accept(await this.requestMicrophone(deviceId));
    if (version !== this.switchVersion) { stream.getTracks().forEach(track => track.stop()); return false; }
    const previous = this.sources.mic;
    this.attachSource('mic', stream);
    this.microphoneStream = stream;
    this.microphoneId = deviceId;
    this.detachSource(previous);
    this.emitLevels();
    return true;
  }

  emitLevels() {
    if (this.closed) return;
    const now = performance.now();
    const elapsed = this.lastMeterAt === undefined ? 0 : Math.max(0, Math.min(500, now - this.lastMeterAt));
    this.lastMeterAt = now;
    const levels = {};
    for (const [kind, source] of Object.entries(this.sources)) {
      let dbfs = -120, measured = false;
      if (source.analyser?.getFloatTimeDomainData && this.context?.state === 'running') {
        source.analyser.getFloatTimeDomainData(source.data);
        const mean = source.data.reduce((total, value) => total + value, 0) / source.data.length;
        const energy = source.data.reduce((total, value) => total + (value - mean) ** 2, 0) / source.data.length;
        dbfs = energy > 0 ? Math.max(-120, 10 * Math.log10(energy)) : -120;
        measured = true;
      }
      const ended = source.track?.readyState === 'ended', muted = Boolean(source.track?.muted);
      const detected = measured && !ended && !muted && dbfs > -50;
      if (detected && !this.paused) source.seen = true;
      if (measured && !this.paused) source.measured = true;
      if (this.monitoring && !this.paused && measured && !ended && !muted && elapsed) {
        source.samples.push({ milliseconds: elapsed, signal: detected ? elapsed : 0 });
        let duration = source.samples.reduce((sum, sample) => sum + sample.milliseconds, 0);
        while (duration > 10000 && source.samples.length > 1) duration -= source.samples.shift().milliseconds;
        const sound = source.samples.reduce((sum, sample) => sum + sample.signal, 0);
        // A click or short noise peak must not hide a persistently quiet source.
        // This checks input level, never whether intelligible speech is present.
        source.lowSignal = duration >= 8000 && sound < 300;
      }
      levels[kind] = { level: ended || muted ? 0 : Math.round(Math.max(0, Math.min(100, (dbfs + 60) / 60 * 100))),
        seen: source.seen, lowSignal: source.lowSignal,
        status: ended ? 'ended' : muted ? 'muted' : !measured ? 'unmeasured' : detected ? 'sound' : 'waiting' };
    }
    this.levels = levels;
    this.onLevels(levels);
  }

  resetSignals() {
    this.monitoring = true; this.paused = false; this.lastMeterAt = performance.now();
    Object.values(this.sources).forEach(source => { source.seen = false; source.measured = false; source.samples = []; source.lowSignal = false; });
  }

  setPaused(paused) { this.paused = paused; this.lastMeterAt = performance.now(); }

  diagnostics() {
    return { recording_source: this.input,
      ...(this.sources.mic ? { mic_signal_detected: this.sources.mic.seen, mic_signal_measured: this.sources.mic.measured,
        mic_low_signal: this.sources.mic.lowSignal } : {}),
      ...(this.sources.tab ? { tab_signal_detected: this.sources.tab.seen, tab_signal_measured: this.sources.tab.measured,
        tab_low_signal: this.sources.tab.lowSignal, capture_surface: this.displaySurface } : {}) };
  }

  close() {
    if (this.closed) return;
    this.closed = true;
    this.switchVersion++;
    clearInterval(this.meterTimer);
    Object.values(this.sources).forEach(source => this.detachSource(source, false));
    const tracks = new Set([this.displayStream, this.microphoneStream, this.tabStream, this.outputStream].flatMap(stream => stream?.getTracks() || []));
    tracks.forEach(track => { try { track.stop(); } catch {} });
    try { this.context?.close()?.catch(() => {}); } catch {}
  }
}
